// Structural invariants of a Score. Validation reports rather than throws —
// converters and importers surface issues; editors keep them at zero.

import { compareF, frac, measureQuarters, mulF, PPQ } from './duration';
import { voiceLength } from './timing';
import { isPartMeasurePlaybackRule } from './playbackRules';
import type { Score, VoiceItem } from './types';

export interface ScoreIssue {
  severity: 'error' | 'warning';
  message: string;
}

export function validateScore(score: Score): ScoreIssue[] {
  const issues: ScoreIssue[] = [];
  const eventLocations = new Map<string, string>();
  const eventIds = new Set<string>();
  if (score.form.length === 0) {
    issues.push({ severity: 'error', message: 'Score has no measures' });
  }
  if (!score.form[0]?.timeSig) {
    issues.push({ severity: 'error', message: 'Measure 1 has no time signature' });
  }

  // Feel cues (#173): a cue anchored past the end of its own measure is not a
  // cue for the next bar, it is invalid data — the resolver would either drop
  // it or silently re-anchor it, and both hide an authoring mistake.
  let cueMeter: [number, number] = score.form[0]?.timeSig ?? [4, 4];
  score.form.forEach((measure, i) => {
    cueMeter = measure.timeSig ?? cueMeter;
    const bandCue = measure.bandCue;
    if (bandCue) {
      if (!['play', 'rest', 'hit'].includes(bandCue.mode)) {
        issues.push({ severity: 'error', message: `Measure ${i + 1}: Unknown band cue` });
      }
      if (
        bandCue.channels !== undefined &&
        (!Array.isArray(bandCue.channels) ||
          bandCue.channels.length === 0 ||
          bandCue.channels.some((channel) => !['comp', 'bass', 'drums'].includes(channel)))
      ) {
        issues.push({ severity: 'error', message: `Measure ${i + 1}: Invalid band cue channels` });
      }
      if (bandCue.duration !== undefined) {
        const num = bandCue.duration?.num;
        const den = bandCue.duration?.den;
        const capacity =
          measure.pickupBeats != null
            ? mulF(frac(measure.pickupBeats), frac(4, cueMeter[1]))
            : measureQuarters(cueMeter);
        if (
          !Number.isInteger(num) ||
          !Number.isInteger(den) ||
          num <= 0 ||
          den <= 0 ||
          compareF(bandCue.duration, capacity) > 0
        ) {
          issues.push({
            severity: 'error',
            message: `Measure ${i + 1}: Band hit duration is outside the bar`,
          });
        }
      }
    }
    const cue = measure.feel;
    if (!cue) return;
    if (typeof cue.label !== 'string' || !cue.label.trim()) {
      issues.push({ severity: 'error', message: `Measure ${i + 1}: Feel cue has no label` });
    }
    if (!cue.offset) return;
    const { num, den } = cue.offset;
    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0 || num < 0) {
      issues.push({ severity: 'error', message: `Measure ${i + 1}: Feel cue offset is malformed` });
      return;
    }
    if (compareF(cue.offset, measureQuarters(cueMeter)) >= 0) {
      issues.push({
        severity: 'error',
        message: `Measure ${i + 1}: Feel cue offset falls outside the ${cueMeter[0]}/${cueMeter[1]} bar`,
      });
    }
  });

  for (const part of score.parts) {
    if (part.measures.length !== score.form.length) {
      issues.push({
        severity: 'error',
        message: `Part "${part.name}" has ${part.measures.length} measures; form has ${score.form.length}`,
      });
    }
    if (part.staves.length === 0) {
      issues.push({ severity: 'error', message: `Part "${part.name}" has no staves` });
    }

    let timeSig: [number, number] = score.form[0]?.timeSig ?? [4, 4];
    part.measures.forEach((measure, i) => {
      timeSig = score.form[i]?.timeSig ?? timeSig;
      const capacity = score.form[i]?.pickupBeats
        ? mulF(frac(Math.round(score.form[i].pickupBeats * PPQ), PPQ), frac(4, timeSig[1]))
        : measureQuarters(timeSig);
      if (measure.playback !== undefined && (
        !Array.isArray(measure.playback) ||
        measure.playback.some((rule) => !isPartMeasurePlaybackRule(rule) ||
          (rule.staff !== undefined && rule.staff >= part.staves.length) ||
          (rule.startOffset !== undefined && compareF(rule.startOffset, capacity) >= 0) ||
          (rule.endOffset !== undefined && compareF(rule.endOffset, capacity) > 0))
      )) {
        issues.push({ severity: 'error', message: `Part "${part.name}" measure ${i + 1}: Invalid playback rule` });
      }
      for (const voice of measure.voices) {
        if (compareF(voiceLength(voice), capacity) > 0) {
          issues.push({
            severity: 'warning',
            message: `Part "${part.name}" measure ${i + 1}: voice overflows the ${timeSig[0]}/${timeSig[1]} bar`,
          });
        }
        const staffCount = part.staves.length;
        if (!isStaffIndex(voice.staff ?? 0, staffCount)) {
          issues.push({
            severity: 'error',
            message: `Part "${part.name}" measure ${i + 1}: voice references staff ${voice.staff} of ${staffCount}`,
          });
        }
        collectEventData(
          voice.items,
          `Part "${part.name}" measure ${i + 1} voice "${voice.id}"`,
          staffCount,
          eventIds,
          eventLocations,
          issues,
        );
      }
    });
  }

  for (const spanner of score.spanners ?? []) {
    for (const ref of [spanner.fromEventId, spanner.toEventId]) {
      if (!eventIds.has(ref)) {
        issues.push({
          severity: 'error',
          message: `Spanner ${spanner.id} references missing event ${ref}`,
        });
      }
    }
  }
  return issues;
}

function isStaffIndex(value: number, staffCount: number): boolean {
  return Number.isInteger(value) && value >= 0 && value < staffCount;
}

function collectEventData(
  items: VoiceItem[],
  location: string,
  staffCount: number,
  eventIds: Set<string>,
  eventLocations: Map<string, string>,
  issues: ScoreIssue[],
): void {
  for (const item of items) {
    if (item.kind === 'tuplet') {
      collectEventData(item.items, location, staffCount, eventIds, eventLocations, issues);
      continue;
    }

    const firstLocation = eventLocations.get(item.id);
    if (firstLocation) {
      issues.push({
        severity: 'error',
        message: `Duplicate event ID "${item.id}" at ${location}; first used at ${firstLocation}`,
      });
    } else {
      eventLocations.set(item.id, location);
      eventIds.add(item.id);
    }

    if (item.slash !== undefined && (
      !['beat', 'rhythm'].includes(item.slash) || item.kind !== 'note' || item.notes.length !== 0
    )) {
      issues.push({ severity: 'error', message: `${location}: event "${item.id}" has invalid unpitched slash notation` });
    }

    if (item.staff !== undefined && !isStaffIndex(item.staff, staffCount)) {
      issues.push({
        severity: 'error',
        message: `${location}: event "${item.id}" references staff ${item.staff} of ${staffCount}`,
      });
    }
  }
}
